"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Cpu, Code, Zap, Shield, Settings, Activity } from "lucide-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaUserTie, FaUserAlt } from "react-icons/fa";
import TechStackAccordionForEmbedded from "./TechStackAccordionForEmbedded";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const embeddedSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Embedded Systems Development",
  "provider": { "@type": "Organization", "name": "Fiieve", "url": "https://fiieve.com" },
  "description": "Fiieve delivers real-time embedded firmware for ARM Cortex, STM32, AVR, and RISC-V platforms using bare-metal C/C++ and FreeRTOS — from driver development to full system integration.",
  "areaServed": "GB",
  "serviceType": "Embedded Systems Development",
  "url": "https://fiieve.com/engineering/embedded-systems",
  "offers": {
    "@type": "Offer",
    "description": "Embedded firmware development for ARM Cortex, STM32, AVR, RISC-V using bare-metal C/C++, FreeRTOS, and Zephyr RTOS."
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",                    "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services",                "item": "https://fiieve.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Engineering",             "item": "https://fiieve.com/services#engineering" },
      { "@type": "ListItem", "position": 4, "name": "Embedded Systems",        "item": "https://fiieve.com/engineering/embedded-systems" }
    ]
  }
});

const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

function CountUp({ target, duration = 1200 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  useEffect(() => {
    if (!inView) return;
    if (target.includes("–") || target.includes("/")) {
      const t = setTimeout(() => setDisplay(target), 400);
      return () => clearTimeout(t);
    }
    const numeric = parseFloat(target.replace(/[^\d.]/g, ""));
    const suffix  = target.replace(/[\d.]/g, "");
    const isDecimal = target.includes(".");
    let start = 0;
    const inc = numeric / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= numeric) {
        setDisplay(`${isDecimal ? numeric.toFixed(1) : numeric}${suffix}`);
        clearInterval(timer);
      } else {
        setDisplay(`${isDecimal ? start.toFixed(1) : Math.floor(start)}${suffix}`);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref}>{display}</span>;
}

function ServiceCard({ icon: Icon, title, description, isDark }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 200, damping: 12 }}
      className={`${isDark ? "bg-gray-800" : "bg-[#1a2e1a]"} border-2 border-green-400 rounded-lg p-6 hover:border-green-300 transition-all duration-300 h-full`}
    >
      <div className="flex items-start gap-4 w-full">
        <div className="bg-green-400 p-2.5 rounded-lg flex-shrink-0 self-start">
          <Icon className="w-6 h-6 text-black" />
        </div>
        <div>
          <h3 className="text-green-400 text-base font-bold mb-2 leading-tight">{title}</h3>
          <p className="text-gray-300 text-xs leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function EmbeddedSystemsPage() {
  const { isDark } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const headingText  = isDark ? "text-white"     : "text-gray-900";
  const bodyText     = isDark ? "text-gray-400"  : "text-gray-600";
  const statCardBg   = isDark ? "bg-gray-800/80" : "bg-[#1a2e1a]";
  const testimCardBg = isDark ? "bg-[#0a0a0a]"  : "bg-[#1a2e1a]";
  const ctaBoxBg     = isDark ? "bg-black"       : "bg-[#1a2e1a]";
  const techCardBg   = isDark ? "bg-black"       : "bg-[#1a2e1a]";

  // ✅ SEO: SRS description keywords
  const services = [
    {
      icon: Cpu,
      title: "ARM Cortex & STM32 Firmware",
      description: "Bare-metal and FreeRTOS-based firmware for STM32, ARM Cortex-M0 through M7 microcontrollers — including HAL driver configuration, clock setup, and peripheral integration.",
    },
    {
      icon: Code,
      title: "AVR & RISC-V Development",
      description: "Firmware development for AVR (ATmega, ATtiny) and RISC-V platforms — from bare-metal C/C++ register-level programming to library integration and bootloader development.",
    },
    {
      icon: Zap,
      title: "Real-Time Operating Systems",
      description: "FreeRTOS and Zephyr RTOS integration — task scheduling, inter-task communication, semaphores, mutexes, and deterministic real-time behaviour for safety-critical applications.",
    },
    {
      icon: Settings,
      title: "Driver & Peripheral Development",
      description: "Custom driver development for SPI, I2C, UART, CAN, USB, ADC, and custom communication protocols — tested against hardware specifications for reliable peripheral control.",
    },
    {
      icon: Activity,
      title: "Low-Power & Optimisation",
      description: "Power profiling and low-power firmware design for battery-operated devices — sleep modes, wake-up strategies, clock gating, and energy budgeting for long field life.",
    },
    {
      icon: Shield,
      title: "Testing, Validation & OTA Updates",
      description: "Unit testing with Unity and CMock, hardware-in-the-loop (HIL) testing, and secure over-the-air (OTA) firmware update implementation for production-ready embedded systems.",
    },
  ];

  const stats = [
    { number: "25+",  label: "Embedded Projects",     icon: "/Images/satisfaction.png" },
    { number: "10+",  label: "MCU Families Supported", icon: "/Images/roi.png" },
    { number: "99%",  label: "Real-Time Reliability",  icon: "/Images/time.png" },
    { number: "24/7", label: "Technical Support",      icon: "/Images/award.png" },
  ];

  const testimonials = [
    {
      id: 1, name: "David Clarke", handle: "@david_embedded", gender: "male",
      title: "Flawless STM32 Firmware",
      feedback: "Fiieve delivered rock-solid FreeRTOS firmware for our STM32-based industrial controller. The code quality, documentation, and communication throughout the project were all exceptional.",
    },
    {
      id: 2, name: "Nadia Hussain", handle: "@nadia_hardware", gender: "female",
      title: "Expert Low-Power Design",
      feedback: "They designed our battery-powered sensor firmware with incredible power efficiency. What would have taken us months internally was delivered in weeks, ready for production.",
    },
  ];

  const technologies = [
    {
      title: "Microcontroller Platforms",
      description: "Industry-standard MCU families for embedded firmware development",
      tools: ["STM32 (ARM Cortex-M)", "AVR (ATmega, ATtiny)", "RISC-V (ESP32-C3)", "NXP Kinetis", "Nordic nRF52"],
      benefits: ["Wide peripheral support", "Low-power modes", "Strong community and tooling", "Production-proven reliability"],
    },
    {
      title: "Firmware & Development Tools",
      description: "Professional embedded development tools and RTOS frameworks",
      tools: ["FreeRTOS", "Zephyr RTOS", "STM32CubeIDE", "GCC ARM Toolchain", "OpenOCD / J-Link"],
      benefits: ["Deterministic real-time execution", "Hardware abstraction layers", "JTAG/SWD debug support", "Automated test frameworks"],
    },
  ];

  // ✅ SEO: tech tags from SRS
  const techTags = ["ARM Cortex", "STM32", "AVR", "RISC-V", "FreeRTOS", "Zephyr", "C/C++"];

  const sliderSettings = {
    dots: true, infinite: false, speed: 800, slidesToShow: 2, slidesToScroll: 1, autoplay: false,
    responsive: [{ breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } }],
  };

  return (
    <>
      <SEOHead
        title="Embedded Systems Development UK | STM32, ARM, FreeRTOS | Fiieve"
        description="Fiieve delivers real-time embedded firmware for ARM Cortex, STM32, AVR, and RISC-V platforms using bare-metal C/C++ and FreeRTOS. Driver development, low-power design, and OTA updates."
        canonical="https://fiieve.com/engineering/embedded-systems"
        ogImage="https://fiieve.com/Images/og-embedded.jpg"
        schema={embeddedSchema}
      />

      <main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"}`}
        style={isDark ? darkGridStyle : lightGridStyle}
      >
        {!isDark && (
          <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"-10%", left:"-5%",  width:"55vw", height:"55vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(40px)" }} />
            <div style={{ position:"absolute", top:"-8%",  right:"-8%", width:"50vw", height:"50vw", background:"radial-gradient(circle, rgba(251,146,60,0.11) 0%, transparent 70%)",  filter:"blur(40px)" }} />
            <div style={{ position:"absolute", top:"45%",  left:"20%",  width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg2.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
                Embedded Systems
              </h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white" role="list">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li><a href="/services" className="hover:text-green-400 transition-colors">Services</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li><span className="text-gray-400">Engineering</span></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Embedded Systems</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── INTRO ── */}
          <motion.section
            aria-labelledby="embedded-intro-heading"
            className="pt-10 pb-4 px-6 max-w-7xl mx-auto text-center"
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-2`}>
              ARM Cortex • STM32 • AVR • RISC-V • FreeRTOS
            </p>
            <h2
              id="embedded-intro-heading"
              className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} mb-5`}
            >
              From Register to Runtime —{" "}
              <span className={greenHeading}>Real-Time Firmware Built for Reliability</span>
            </h2>
            {/* ✅ SEO: exact SRS description */}
            <p className={`${bodyText} text-lg md:text-xl leading-relaxed mb-8`}>
              Whether you need bare-metal drivers for a custom MCU, a deterministic RTOS implementation, or a complete embedded software stack for a production device, Fiieve delivers real-time firmware for{" "}
              <strong className={headingText}>ARM Cortex</strong>,{" "}
              <strong className={headingText}>STM32</strong>,{" "}
              <strong className={headingText}>AVR</strong>, and{" "}
              <strong className={headingText}>RISC-V</strong> platforms using{" "}
              <strong className={headingText}>bare-metal C/C++</strong> and{" "}
              <strong className={headingText}>FreeRTOS</strong>. From initial hardware bring-up to production-ready firmware with OTA updates, we manage the complete embedded development lifecycle.
            </p>
            {/* ✅ SEO: tech tag pills */}
            <div
              className="inline-flex flex-wrap justify-center items-center gap-1 p-1.5 rounded-full"
              style={{ background: isDark ? "#111" : "#0f1f0f", border: "1px solid rgba(74,222,128,0.25)" }}
              aria-label="Technologies used in embedded systems development"
            >
              {techTags.map((tag) => (
                <span key={tag} className="px-4 py-1.5 rounded-full text-base font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 cursor-default">
                  {tag}
                </span>
              ))}
            </div>
          </motion.section>

          {/* ── SERVICES ── */}
          <section aria-labelledby="services-heading" className="pt-8 pb-12 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-1`}>What We Build</h2>
                <h2 id="services-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>
                  Our Embedded Systems Services
                </h2>
              </div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              >
                {services.map((s, i) => (
                  <ServiceCard key={i} icon={s.icon} title={s.title} description={s.description} isDark={isDark} />
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── STATS ── */}
          <section aria-labelledby="stats-heading" className="pb-16">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <motion.div className="mb-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Track Record</h2>
                <h2 id="stats-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>
                  Proven Embedded Results
                </h2>
              </motion.div>
              <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                {stats.map((card, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.08, boxShadow: "0 0 30px rgba(74,222,128,0.6)" }}
                    transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className={`border border-green-400/50 rounded-2xl p-8 text-center hover:border-green-400 transition-all duration-300 ${statCardBg} flex flex-col items-center`}
                  >
                    <img src={card.icon} alt={card.label} className="w-20 h-20 mb-4 object-contain" loading="lazy" width={80} height={80} />
                    <div className="text-4xl font-extrabold text-green-400 mb-2"><CountUp target={card.number} /></div>
                    <p className="text-gray-300 text-lg font-semibold">{card.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── TESTIMONIALS ── */}
          <section aria-labelledby="testimonials-heading">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-6xl mx-auto px-4 mb-16">
              <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} text-center mb-2`}>Client Results</h2>
              <h2 id="testimonials-heading" className={`${greenHeading} text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12`}>
                What Our Clients Say
              </h2>
              {!isDark && <style>{`.slick-prev:before, .slick-next:before { color: #166534 !important; }`}</style>}
              <Slider {...sliderSettings}>
                {testimonials.map((t) => (
                  <div key={t.id} className="px-3">
                    <motion.article
                      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                      aria-label={`Testimonial from ${t.name}`}
                      className={`${testimCardBg} border border-green-400/30 rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-green-400/10 hover:border-green-400/60 transition-all duration-300 flex flex-col sm:flex-row items-center sm:items-start gap-5`}
                    >
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-green-400/40 bg-[#0d1a0f] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        {t.gender === "male" ? <FaUserTie size={28} className="text-green-400" /> : <FaUserAlt size={24} className="text-green-300" />}
                      </div>
                      <div className="text-center sm:text-left flex flex-col gap-2">
                        <h3 className="text-lg font-bold text-white">{t.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{t.feedback}</p>
                        <div className="flex items-center justify-center sm:justify-start gap-0.5 mt-1" aria-label="5 star rating">
                          {Array(5).fill().map((_, i) => <span key={i} className="text-green-400 text-lg" aria-hidden="true">★</span>)}
                        </div>
                        <p className="text-green-400 font-semibold text-sm">{t.name}</p>
                        <p className="text-gray-500 text-xs">{t.handle}</p>
                      </div>
                    </motion.article>
                  </div>
                ))}
              </Slider>
            </motion.div>
          </section>

          {/* ── CTA ── */}
          <motion.section
            aria-label="Start your embedded systems project"
            initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto px-4 mb-16"
          >
            <div className={`${ctaBoxBg} border-2 border-green-400 rounded-2xl p-10 flex flex-col items-center gap-6 text-center`}>
              <div className="w-full max-w-3xl">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Ready to Build Your Embedded System?
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  Whether you need bare-metal firmware, a FreeRTOS RTOS implementation, or a production-ready embedded software stack — our engineers deliver reliable, real-time systems. Get in touch today.
                </p>
              </div>
              <button
                onClick={() => navigate("/contact-us")}
                aria-label="Contact Fiieve to start your embedded systems project"
                className="bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300"
              >
                Discuss Your Embedded Project
              </button>
            </div>
          </motion.section>

          {/* ── TECHNOLOGIES ── */}
          <section aria-labelledby="tech-heading" className="py-6 md:py-8 px-4 -mt-12 md:-mt-16">
            <div className="max-w-7xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Embedded Stack</h2>
                <h2 id="tech-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold mb-4`}>
                  Technologies We Use
                </h2>
                <p className={`${bodyText} text-lg max-w-2xl mx-auto`}>
                  We use proven microcontroller platforms, RTOS frameworks, and professional development tools to build reliable embedded systems.
                </p>
              </motion.div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.15 } } }}
              >
                {technologies.map((tech, i) => (
                  <motion.article
                    key={i}
                    aria-label={`${tech.title} technology stack`}
                    className={`${techCardBg} border-2 border-green-400 rounded-2xl p-6 transition-all duration-300`}
                    whileHover={{ scale: 1.05, boxShadow: "0 0 25px 5px rgba(74,222,128,0.7)", borderColor: "#4ade80" }}
                    transition={{ type: "spring", stiffness: 200, damping: 12 }}
                  >
                    <h3 className="text-green-400 text-xl font-bold mb-2">{tech.title}</h3>
                    <p className="text-gray-400 mb-3">{tech.description}</p>
                    <p className="text-gray-300 font-semibold text-sm mb-1">Platforms & Tools:</p>
                    <p className="text-gray-300 mb-3">{tech.tools.join(", ")}</p>
                    <p className="text-gray-300 font-semibold text-sm mb-1">Benefits:</p>
                    <ul className="list-disc list-inside text-gray-300">
                      {tech.benefits.map((b, idx) => <li key={idx}>{b}</li>)}
                    </ul>
                  </motion.article>
                ))}
              </motion.div>
            </div>
          </section>

          <div className="py-12 px-4">
            <TechStackAccordionForEmbedded />
          </div>

        </div>
      </main>
    </>
  );
}